import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ChevronRightSmallIcon } from '@inter-ikea-kompis/icons';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { CheckboxAlignButtonEnum } from '@inter-ikea-kompis/component-checkbox';
import { CheckboxLayoutEnum } from '@inter-ikea-kompis/component-checkbox';
import { Checkbox } from '@inter-ikea-kompis/component-checkbox';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { RadioLayoutEnum } from '@inter-ikea-kompis/component-radio-button';
import { RadioButton } from '@inter-ikea-kompis/component-radio-button';
import { Text } from '@inter-ikea-kompis/component-text';

import { Switch as SkapaSwitch } from '@ingka/switch-webc/passive.js';

import ActionListBorderEnum from '../../enums/ActionListBorderEnum.js';
import ActionListControlEnum from '../../enums/ActionListControlEnum.js';
import ActionListItemStyle from './ActionListItemStyle.js';

/**
 * Component that can render in an opened or closed state.
 *
 * @event select Triggered when action list or input control is clicked.
 */

export default class ActionListItem extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Label
	@property({ type: String })
	public label: string | null = null;

	// Caption
	@property({ type: String })
	public caption: string | null = null;

	// Leading icon
	@property({ type: String })
	public leadingIcon: string | null = null;

	// Leading Image
	@property({ type: String })
	public imageSrc: string | null = null;

	// Horizontal padding
	@property({ type: Boolean })
	public horizontalPadding = false;

	// Disabled
	@property({ type: Boolean })
	public disabled = false;

	// For the compact variant
	@property({ type: Boolean })
	public compact = false;

	// Controls
	@property({ type: String })
	public control: ActionListControlEnum | null = null;

	// Border for the action list
	@property({ type: String })
	public actionListBorder: ActionListBorderEnum = ActionListBorderEnum.bottom;

	// Label of the checkbox or radio button control
	@property({ type: Number })
	public controlLabel: number | null = null;

	// Is "true" if a control is checked
	@property({ type: Boolean })
	public checked = false;

	// Selected index
	@property({ type: Number })
	public selectedIndex: number | null = null;

	private hovered = false;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${ActionListItemStyle.getStyle({
					horizontalPadding: this.horizontalPadding,
					theme: this.theme,
					disabled: this.disabled,
					compact: this.compact,
					hovered: this.hovered,
					checked: this.checked,
					actionListBorder: this.actionListBorder
				})}
			</style>

			<button
				@click="${this.onClick.bind(this)}"
				@mouseover="${this.onMouseOver.bind(this)}"
				@mouseleave="${this.onMouseOut.bind(this)}"
				?disabled="${this.disabled}"
				tabindex="${this.control === ActionListControlEnum.chevron || this.control === null
					? '0'
					: '-1'}"
			>
				<div class="container">
					${this.getIcon()} ${this.getImage()}
					<div class="textContainer">
						<kompis-text
							class="header"
							.theme="${this.theme}"
							.type="${ThemeFontStyleTypeEnum.bodyMedium}"
							.bold="${true}"
							>${this.label}</kompis-text
						>
						${this.getCaption()}
					</div>
				</div>
				${this.getControl()}
				<div class="outline"></div>
			</button>
			<slot></slot>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-switch', SkapaSwitch);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-icon', Icon);
		this.customElements.define('kompis-checkbox', Checkbox);
		this.customElements.define('kompis-radio-button', RadioButton);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	private onMouseOver(): void {
		if (this.hovered) {
			return;
		}
		this.hovered = true;
		this.requestUpdate();
	}

	private onMouseOut(): void {
		if (!this.hovered) {
			return;
		}
		this.hovered = false;
		this.requestUpdate();
	}
	/**
	 * Returns the caption if provided.
	 */
	private getCaption(): RenderResult {
		if (!this.caption) {
			return null;
		}

		return html`
			<kompis-text
				class="caption"
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.bodyMedium}"
				>${this.caption}</kompis-text
			>
		`;
	}

	/**
	 * Returns an icon if provided.
	 */
	private getIcon(): RenderResult {
		if (!this.leadingIcon) {
			return null;
		} else if (!this.imageSrc) {
			return html`
				<kompis-icon
					.theme="${this.theme}"
					.icon="${this.leadingIcon}"
					class="leadingIcon"
				></kompis-icon>
				<div class="spacing"></div>
			`;
		} else {
			return null;
		}
	}

	/**
	 * Returns an image if provided.
	 */
	private getImage(): RenderResult {
		if (!this.imageSrc) {
			return null;
		} else if (!this.leadingIcon) {
			return html`<img alt="${this.label}" src="${this.imageSrc}" />
				<div class="spacing"></div>`;
		} else {
			return null;
		}
	}

	private getControl(): RenderResult | null {
		switch (this.control) {
			case ActionListControlEnum.checkbox:
				return html`<kompis-checkbox
					@change="${(event: CustomEvent) => this.onClick(event)}"
					.checked="${this.checked}"
					.disabled="${this.disabled}"
					.label="${this.controlLabel ? String(this.controlLabel) : null}"
					.alignButton="${CheckboxAlignButtonEnum.right}"
					.layout="${CheckboxLayoutEnum.subtle}"
					.hovered="${this.hovered}"
					.theme="${this.theme}"
				></kompis-checkbox>`;

			case ActionListControlEnum.chevron:
				return html`
					<kompis-icon
						class="icon"
						tabindex="-1"
						.flipIconRtl="${true}"
						.icon="${ChevronRightSmallIcon}"
						.theme="${this.theme}"
					></kompis-icon>
				`;

			case ActionListControlEnum.radioButton:
				return html`
					<kompis-radio-button
						.checked="${this.checked}"
						.disabled="${this.disabled}"
						.label="${this.controlLabel ? String(this.controlLabel) : null}"
						.alignButton="${CheckboxAlignButtonEnum.right}"
						.layout="${RadioLayoutEnum.subtle}"
						.hovered="${this.hovered}"
						.theme="${this.theme}"
					></kompis-radio-button>
				`;

			case ActionListControlEnum.switch:
				return html`
					<skapa-switch
						subtle
						.checked="${this.checked}"
						.disabled="${this.disabled}"
						auto-id="${`switch-${this.controlLabel}`}"
					>
						<input @change="${(event: CustomEvent) => this.onClick(event)}" />
					</skapa-switch>
				`;

			default:
				return null;
		}
	}

	/**
	 * Called when radio button checked value has changed.
	 *
	 * @param event Event data.
	 */
	private onClick(event: CustomEvent): void {
		event.stopPropagation();
		event.preventDefault();

		if (this.control === ActionListControlEnum.radioButton && this.checked) {
			return;
		}

		const enumsToCheck = [
			ActionListControlEnum.checkbox,
			ActionListControlEnum.radioButton,
			ActionListControlEnum.switch
		];

		this.dispatchEvent(
			new CustomEvent('select', {
				bubbles: true,
				detail: {
					checked: this.control && enumsToCheck.includes(this.control) ? this.checked : null,
					selectedIndex: this.selectedIndex
				}
			})
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-action-list-item': ActionListItem;
		'skapa-switch': SkapaSwitch;
	}
}
